import { Controller, useFormContext } from 'react-hook-form';

import { Autocomplete, Chip, TextField, Tooltip } from '@mui/material';

import { translateErrorMessage } from './FormConsts';

type Option = {
  id: string;
  label: string;
  description?: string;
};

type Props = {
  name: string;
  label: string;
  options: Option[];
  required?: boolean;
  fullWidth?: boolean;
};

export const MultiSelectField = ({
  name,
  label,
  options,
  required = false,
  fullWidth = true
}: Readonly<Props>) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? 'This field is required' : false }}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => (
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={option => option.label}
          value={options.filter(option => value.includes(option.id))}
          onChange={(_, newValue) => {
            onChange(newValue.map(option => option.id));
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '56px',
              padding: '6px 14px',
              '& .MuiAutocomplete-input': {
                height: '44px',
                padding: '0 !important',
                margin: '0 !important'
              },
              '& .MuiAutocomplete-inputRoot': {
                flexWrap: 'nowrap',
                overflow: 'hidden',
                alignItems: 'center',
                height: '44px',
                padding: '0'
              },
              '& .MuiAutocomplete-tag': {
                margin: '1px 2px 1px 0',
                height: '22px',
                '& .MuiChip-root': {
                  height: '22px'
                }
              }
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              required={required}
              fullWidth={fullWidth}
              error={!!error}
              helperText={error ? translateErrorMessage(error) : ''}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const tagProps = getTagProps({ index });
              const maxLength = 20;
              const isLongLabel = option.label.length > maxLength;
              const truncatedLabel = isLongLabel
                ? `${option.label.substring(0, maxLength)}...`
                : option.label;

              return (
                <Tooltip key={option.id} title={isLongLabel ? option.label : ''} arrow>
                  <Chip
                    {...tagProps}
                    variant="outlined"
                    label={truncatedLabel}
                    sx={{
                      height: '22px',
                      maxWidth: '120px',
                      fontSize: '0.75rem',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        padding: '0 6px',
                        lineHeight: '20px'
                      },
                      '& .MuiChip-deleteIcon': {
                        fontSize: '14px',
                        height: '14px',
                        width: '14px'
                      }
                    }}
                  />
                </Tooltip>
              );
            })
          }
        />
      )}
    />
  );
};
